import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { customerAuthAPI, reviewAPI } from '../../services/api';
import { User, MapPin, Package, LogOut, Plus, Trash2, Edit2, Save, X, ChevronRight, FileText, Download, Star, MessageSquare } from 'lucide-react';

export default function Account() {
    const { customer, logout, updateCustomer } = useCustomerAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState('profile');
    const [orders, setOrders] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(false);

    // Profile edit
    const [editProfile, setEditProfile] = useState(false);
    const [profileForm, setProfileForm] = useState({ firstName: '', lastName: '', phone: '' });

    // Password change
    const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [pwMsg, setPwMsg] = useState('');

    // Address form
    const [showAddrForm, setShowAddrForm] = useState(false);
    const [editAddr, setEditAddr] = useState(null);
    const [addrForm, setAddrForm] = useState({ title: '', firstName: '', lastName: '', phone: '', city: '', district: '', neighborhood: '', address: '', zipCode: '', isDefault: false });

    // Review state
    const [reviewModal, setReviewModal] = useState(null); // { orderItemId, productName, variantInfo }
    const [reviewForm, setReviewForm] = useState({ rating: 0, comment: '' });
    const [reviewHover, setReviewHover] = useState(0);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [reviewedItems, setReviewedItems] = useState(new Set());

    const [msg, setMsg] = useState('');

    useEffect(() => {
        if (!customer) { navigate('/giris'); return; }
        if (!customer.emailVerified) { navigate('/eposta-dogrula'); return; }
        if (!customer.phone) { navigate('/profil-tamamla'); return; }
        setProfileForm({ firstName: customer.firstName, lastName: customer.lastName, phone: customer.phone || '' });
    }, [customer, navigate]);

    useEffect(() => {
        if (tab === 'orders') loadOrders();
        if (tab === 'addresses') loadAddresses();
    }, [tab]);

    const loadOrders = async () => {
        try {
            const res = await customerAuthAPI.getOrders();
            setOrders(res.data);
            // Load which items have already been reviewed
            try {
                const revRes = await reviewAPI.getReviewable();
                const reviewableIds = new Set((revRes.data || []).map(r => r.orderItemId));
                // Items NOT in reviewable = already reviewed
                const allDeliveredItemIds = [];
                (res.data || []).filter(o => o.status === 'delivered').forEach(o => {
                    (o.items || []).forEach(item => allDeliveredItemIds.push(item.id));
                });
                const reviewed = new Set(allDeliveredItemIds.filter(id => !reviewableIds.has(id)));
                setReviewedItems(reviewed);
            } catch (e) { /* ignore */ }
        } catch (e) { console.error(e); }
    };

    const loadAddresses = async () => {
        try {
            const res = await customerAuthAPI.getAddresses();
            setAddresses(res.data);
        } catch (e) { console.error(e); }
    };

    const handleProfileSave = async () => {
        try {
            const res = await customerAuthAPI.updateProfile(profileForm);
            updateCustomer(res.data.customer);
            setEditProfile(false);
            setMsg('Profil güncellendi');
            setTimeout(() => setMsg(''), 3000);
        } catch (e) {
            setMsg(e.response?.data?.error || 'Hata oluştu');
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPwMsg('');
        if (pwForm.newPassword !== pwForm.confirmPassword) {
            return setPwMsg('Şifreler eşleşmiyor');
        }
        try {
            await customerAuthAPI.changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
            setPwMsg('Şifre başarıyla değiştirildi');
            setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (e) {
            setPwMsg(e.response?.data?.error || 'Hata oluştu');
        }
    };

    const handleAddrSave = async () => {
        try {
            if (editAddr) {
                await customerAuthAPI.updateAddress(editAddr.id, addrForm);
            } else {
                await customerAuthAPI.createAddress(addrForm);
            }
            setShowAddrForm(false);
            setEditAddr(null);
            loadAddresses();
        } catch (e) {
            setMsg(e.response?.data?.error || 'Hata oluştu');
        }
    };

    const handleAddrDelete = async (id) => {
        if (!confirm('Bu adresi silmek istediğinize emin misiniz?')) return;
        try {
            await customerAuthAPI.deleteAddress(id);
            loadAddresses();
        } catch (e) { console.error(e); }
    };

    const openAddrEdit = (addr) => {
        setEditAddr(addr);
        setAddrForm({ title: addr.title, firstName: addr.firstName, lastName: addr.lastName, phone: addr.phone, city: addr.city, district: addr.district, neighborhood: addr.neighborhood || '', address: addr.address, zipCode: addr.zipCode || '', isDefault: addr.isDefault });
        setShowAddrForm(true);
    };

    const openNewAddr = () => {
        setEditAddr(null);
        setAddrForm({ title: 'Adresim', firstName: customer?.firstName || '', lastName: customer?.lastName || '', phone: customer?.phone || '', city: '', district: '', neighborhood: '', address: '', zipCode: '', isDefault: false });
        setShowAddrForm(true);
    };

    const statusLabels = {
        pending: 'Beklemede', paid: 'Siparişiniz Alındı', processing: 'Hazırlanıyor',
        shipped: 'Kargoda', delivered: 'Teslim Edildi'
    };
    const statusColors = {
        pending: '#f39c12', paid: '#3498db', processing: '#e67e22',
        shipped: '#9b59b6', delivered: '#2ecc71'
    };

    const openReviewModal = (item) => {
        setReviewModal(item);
        setReviewForm({ rating: 0, comment: '' });
        setReviewHover(0);
    };

    const submitReview = async () => {
        if (!reviewForm.rating || !reviewForm.comment.trim()) {
            setMsg('Lütfen puan verin ve yorum yazın');
            setTimeout(() => setMsg(''), 3000);
            return;
        }
        setReviewLoading(true);
        try {
            await reviewAPI.create({
                orderItemId: reviewModal.orderItemId,
                rating: reviewForm.rating,
                comment: reviewForm.comment
            });
            setReviewedItems(prev => new Set([...prev, reviewModal.orderItemId]));
            setReviewModal(null);
            setMsg('Yorumunuz gönderildi! Onaylandıktan sonra yayınlanacaktır.');
            setTimeout(() => setMsg(''), 4000);
        } catch (e) {
            setMsg(e.response?.data?.error || 'Yorum gönderilemedi');
            setTimeout(() => setMsg(''), 3000);
        }
        setReviewLoading(false);
    };

    const formatPrice = (p) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(p);

    if (!customer) return null;

    return (
        <div className="account-page">
            <div className="account-container">
                <div className="account-sidebar">
                    <div className="account-user">
                        <div className="account-avatar">{customer.firstName[0]}{customer.lastName[0]}</div>
                        <div>
                            <strong>{customer.firstName} {customer.lastName}</strong>
                            <span>{customer.email}</span>
                        </div>
                    </div>
                    <nav className="account-nav">
                        <button className={tab === 'profile' ? 'active' : ''} onClick={() => setTab('profile')}><User size={18} /> Profilim</button>
                        <button className={tab === 'orders' ? 'active' : ''} onClick={() => setTab('orders')}><Package size={18} /> Siparişlerim</button>
                        <button className={tab === 'addresses' ? 'active' : ''} onClick={() => setTab('addresses')}><MapPin size={18} /> Adreslerim</button>
                        <button onClick={() => { logout(); navigate('/'); }} className="account-logout"><LogOut size={18} /> Çıkış Yap</button>
                    </nav>
                </div>

                <div className="account-content">
                    {msg && <div className="account-msg">{msg}</div>}

                    {tab === 'profile' && (
                        <div className="account-section">
                            <h2>Profil Bilgileri</h2>
                            <div className="account-card">
                                {editProfile ? (
                                    <div className="account-form-grid">
                                        <input placeholder="Ad" value={profileForm.firstName} onChange={e => setProfileForm({...profileForm, firstName: e.target.value})} />
                                        <input placeholder="Soyad" value={profileForm.lastName} onChange={e => setProfileForm({...profileForm, lastName: e.target.value})} />
                                        <input placeholder="Telefon" value={profileForm.phone} onChange={e => setProfileForm({...profileForm, phone: e.target.value})} />
                                        <div className="account-form-actions">
                                            <button className="btn btn-primary btn-sm" onClick={handleProfileSave}><Save size={16} /> Kaydet</button>
                                            <button className="btn btn-secondary btn-sm" onClick={() => setEditProfile(false)}><X size={16} /> İptal</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="account-info-row"><span>Ad Soyad</span><strong>{customer.firstName} {customer.lastName}</strong></div>
                                        <div className="account-info-row"><span>E-posta</span><strong>{customer.email}</strong></div>
                                        <div className="account-info-row"><span>Telefon</span><strong>{customer.phone || '-'}</strong></div>
                                        <button className="btn btn-secondary btn-sm" onClick={() => setEditProfile(true)} style={{marginTop:'1rem'}}><Edit2 size={16} /> Düzenle</button>
                                    </>
                                )}
                            </div>

                            <h3 style={{marginTop:'2rem'}}>Şifre Değiştir</h3>
                            <div className="account-card">
                                <form onSubmit={handlePasswordChange} className="account-form-grid">
                                    <input type="password" placeholder="Mevcut şifre" value={pwForm.currentPassword} onChange={e => setPwForm({...pwForm, currentPassword: e.target.value})} required />
                                    <input type="password" placeholder="Yeni şifre" value={pwForm.newPassword} onChange={e => setPwForm({...pwForm, newPassword: e.target.value})} required />
                                    <input type="password" placeholder="Yeni şifre tekrar" value={pwForm.confirmPassword} onChange={e => setPwForm({...pwForm, confirmPassword: e.target.value})} required />
                                    {pwMsg && <p style={{color: pwMsg.includes('başarı') ? 'var(--success)' : 'var(--danger)', gridColumn:'1/-1'}}>{pwMsg}</p>}
                                    <button type="submit" className="btn btn-primary btn-sm">Şifreyi Değiştir</button>
                                </form>
                            </div>
                        </div>
                    )}

                    {tab === 'orders' && (
                        <div className="account-section">
                            <h2>Siparişlerim</h2>
                            {orders.length === 0 ? (
                                <div className="account-empty"><Package size={48} strokeWidth={1} /><p>Henüz siparişiniz yok</p></div>
                            ) : (
                                <div className="account-orders">
                                    {orders.map(order => (
                                        <div key={order.id} className="account-order-card">
                                            <div className="account-order-header">
                                                <div>
                                                    <strong>#{order.orderNumber}</strong>
                                                    <span>{new Date(order.createdAt).toLocaleDateString('tr-TR')}</span>
                                                </div>
                                                <span className="order-status-badge" style={{background: statusColors[order.status] || '#999'}}>
                                                    {statusLabels[order.status] || order.status}
                                                </span>
                                            </div>
                                            <div className="account-order-items">
                                                {(order.items || []).map(item => (
                                                    <div key={item.id} className="account-order-item">
                                                        <div style={{flex:1}}>
                                                            <span>{item.productName} {item.variantInfo ? `(${item.variantInfo})` : ''}</span>
                                                        </div>
                                                        <span style={{whiteSpace:'nowrap'}}>{item.quantity}x {formatPrice(item.unitPrice)}</span>
                                                        {order.status === 'delivered' && (
                                                            reviewedItems.has(item.id) ? (
                                                                <span className="review-done-badge"><Star size={12} fill="var(--warning)" color="var(--warning)" /> Yorumlandı</span>
                                                            ) : (
                                                                <button
                                                                    className="review-btn"
                                                                    onClick={() => openReviewModal({ orderItemId: item.id, productName: item.productName, variantInfo: item.variantInfo })}
                                                                >
                                                                    <Star size={14} /> Yorum Yap
                                                                </button>
                                                            )
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="account-order-footer">
                                                <div style={{display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap'}}>
                                                    <strong>Toplam: {formatPrice(order.totalAmount)}</strong>
                                                    {parseFloat(order.discountAmount) > 0 && (
                                                        <span className="account-order-badge" style={{background:'rgba(231,76,60,.1)', color:'#e74c3c'}}>
                                                            Kupon: {order.couponCode} (-{formatPrice(order.discountAmount)})
                                                        </span>
                                                    )}
                                                    {order.invoiceType === 'corporate' && <span className="account-order-badge">Kurumsal Fatura</span>}
                                                </div>
                                                <div style={{display:'flex', alignItems:'center', gap:'0.75rem', flexWrap:'wrap'}}>
                                                    {order.trackingNumber && (
                                                        order.trackingUrl
                                                            ? <a href={order.trackingUrl} target="_blank" rel="noopener noreferrer" style={{color:'var(--primary)', textDecoration:'underline', fontSize:'0.85rem'}}>Kargo Takip: {order.trackingNumber}</a>
                                                            : <span style={{fontSize:'0.85rem'}}>Kargo Takip No: {order.trackingNumber}</span>
                                                    )}
                                                    {order.invoiceFile && (
                                                        <a href={order.invoiceFile} target="_blank" rel="noopener noreferrer" className="account-invoice-link">
                                                            <FileText size={14} /> Fatura
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'addresses' && (
                        <div className="account-section">
                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem'}}>
                                <h2>Adreslerim</h2>
                                <button className="btn btn-primary btn-sm" onClick={openNewAddr}><Plus size={16} /> Yeni Adres</button>
                            </div>

                            {showAddrForm && (
                                <div className="account-card" style={{marginBottom:'1.5rem'}}>
                                    <h3>{editAddr ? 'Adresi Düzenle' : 'Yeni Adres'}</h3>
                                    <div className="account-form-grid">
                                        <input placeholder="Adres başlığı" value={addrForm.title} onChange={e => setAddrForm({...addrForm, title: e.target.value})} />
                                        <input placeholder="Ad" value={addrForm.firstName} onChange={e => setAddrForm({...addrForm, firstName: e.target.value})} required />
                                        <input placeholder="Soyad" value={addrForm.lastName} onChange={e => setAddrForm({...addrForm, lastName: e.target.value})} required />
                                        <input placeholder="Telefon" value={addrForm.phone} onChange={e => setAddrForm({...addrForm, phone: e.target.value})} required />
                                        <input placeholder="İl" value={addrForm.city} onChange={e => setAddrForm({...addrForm, city: e.target.value})} required />
                                        <input placeholder="İlçe" value={addrForm.district} onChange={e => setAddrForm({...addrForm, district: e.target.value})} required />
                                        <input placeholder="Mahalle" value={addrForm.neighborhood} onChange={e => setAddrForm({...addrForm, neighborhood: e.target.value})} />
                                        <textarea placeholder="Açık adres" value={addrForm.address} onChange={e => setAddrForm({...addrForm, address: e.target.value})} required style={{gridColumn:'1/-1'}} />
                                        <input placeholder="Posta kodu" value={addrForm.zipCode} onChange={e => setAddrForm({...addrForm, zipCode: e.target.value})} />
                                        <label style={{display:'flex', alignItems:'center', gap:'0.5rem'}}>
                                            <input type="checkbox" checked={addrForm.isDefault} onChange={e => setAddrForm({...addrForm, isDefault: e.target.checked})} /> Varsayılan adres
                                        </label>
                                        <div className="account-form-actions">
                                            <button className="btn btn-primary btn-sm" onClick={handleAddrSave}><Save size={16} /> Kaydet</button>
                                            <button className="btn btn-secondary btn-sm" onClick={() => { setShowAddrForm(false); setEditAddr(null); }}><X size={16} /> İptal</button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {addresses.length === 0 && !showAddrForm ? (
                                <div className="account-empty"><MapPin size={48} strokeWidth={1} /><p>Henüz adresiniz yok</p></div>
                            ) : (
                                <div className="account-addresses">
                                    {addresses.map(addr => (
                                        <div key={addr.id} className="account-addr-card">
                                            <div className="account-addr-header">
                                                <strong>{addr.title} {addr.isDefault && <span className="addr-default-badge">Varsayılan</span>}</strong>
                                                <div>
                                                    <button onClick={() => openAddrEdit(addr)}><Edit2 size={16} /></button>
                                                    <button onClick={() => handleAddrDelete(addr.id)}><Trash2 size={16} /></button>
                                                </div>
                                            </div>
                                            <p>{addr.firstName} {addr.lastName}</p>
                                            <p>{addr.address}</p>
                                            <p>{addr.district}, {addr.city} {addr.zipCode}</p>
                                            <p>{addr.phone}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Review Modal */}
            {reviewModal && (
                <div className="review-modal-overlay" onClick={() => setReviewModal(null)}>
                    <div className="review-modal" onClick={e => e.stopPropagation()}>
                        <div className="review-modal-header">
                            <h3>Ürünü Değerlendirin</h3>
                            <button onClick={() => setReviewModal(null)}><X size={20} /></button>
                        </div>
                        <div className="review-modal-product">
                            <strong>{reviewModal.productName}</strong>
                            {reviewModal.variantInfo && <span>{reviewModal.variantInfo}</span>}
                        </div>
                        <div className="review-modal-stars">
                            <span>Puanınız:</span>
                            <div className="review-stars-input">
                                {[1,2,3,4,5].map(s => (
                                    <button
                                        key={s}
                                        type="button"
                                        onMouseEnter={() => setReviewHover(s)}
                                        onMouseLeave={() => setReviewHover(0)}
                                        onClick={() => setReviewForm({...reviewForm, rating: s})}
                                    >
                                        <Star size={28} fill={(reviewHover || reviewForm.rating) >= s ? '#fbbf24' : 'none'} color={(reviewHover || reviewForm.rating) >= s ? '#fbbf24' : 'var(--text-muted)'} />
                                    </button>
                                ))}
                            </div>
                        </div>
                        <textarea
                            className="review-modal-textarea"
                            placeholder="Ürün hakkındaki düşüncelerinizi yazın..."
                            value={reviewForm.comment}
                            onChange={e => setReviewForm({...reviewForm, comment: e.target.value})}
                            rows={4}
                        />
                        <button
                            className="btn btn-primary review-submit-btn"
                            onClick={submitReview}
                            disabled={reviewLoading || !reviewForm.rating || !reviewForm.comment.trim()}
                        >
                            {reviewLoading ? 'Gönderiliyor...' : 'Yorumu Gönder'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
